import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatus } from "~/modules/billing/api/invoiceStatus/invoiceStatusIdTypes.ts";
import type { InvoiceAction, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type {
	InvoiceProgressSegmentState,
} from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/invoiceStatusProgressBarTypes.ts";
import { processedStatuses, wipStatuses } from "~/modules/billing/utils/billingUtils.ts";

type SegmentThemeParams = {
	currentInvoiceStatusId: InvoiceStatusId;
	currentStatusOrderPosition: number;
	isSelfBilledInvoice: boolean;
	recipientIsSet: boolean;
	status: InvoiceStatus;
};

export function getSegmentTheme({
	currentInvoiceStatusId,
	currentStatusOrderPosition,
	isSelfBilledInvoice,
	status,
	recipientIsSet,
}: SegmentThemeParams): InvoiceProgressSegmentState {
	const unavailableStatusesForSelfBilledInvoice: InvoiceStatusId[] = [invoiceStatusId.Draft, invoiceStatusId.AwaitingApproval, invoiceStatusId.Approved];

	if (status.id === invoiceStatusId.Reversed) return "reversed";
	if (status.id === invoiceStatusId.Paid && status.order === currentStatusOrderPosition) return "paid";
	if (status.order === currentStatusOrderPosition) return "active";

	if (isSelfBilledInvoice && unavailableStatusesForSelfBilledInvoice.includes(status.id)) {
		return "status-unavailable";
	}

	if (status.order < currentStatusOrderPosition) return "completed";

	// self billed invoices do not have a recipient id as they are sent by the client
	if (processedStatuses.includes(status.id) && !isSelfBilledInvoice && !recipientIsSet) {
		return "disabled";
	}

	if (status.id === invoiceStatusId.Paid && currentInvoiceStatusId !== invoiceStatusId.SentToClient) {
		return "disabled";
	}

	return "upcoming";
}

type GetTooltipTextForSegmentParams = {
	currentStatusId: InvoiceStatusId;
	isSelfBilledInvoice: boolean;
	statusId: InvoiceStatusId;
	theme: InvoiceProgressSegmentState;
};

export function getTooltipTextForSegment({
	currentStatusId,
	isSelfBilledInvoice,
	statusId,
	theme,
}: GetTooltipTextForSegmentParams) {
	if (isSelfBilledInvoice && theme === "status-unavailable") {
		return "Dieser Status ist für Gutschriften nicht verfügbar";
	}

	if (statusId === invoiceStatusId.Paid && !processedStatuses.includes(currentStatusId) && theme === "disabled") {
		return "Dieser Status kann erst ausgewählt werden, wenn die Rechnung an den Kunden gesendet wurde";
	}

	if (statusId === invoiceStatusId.SentToClient && theme === "disabled") {
		return "Dieser Status kann erst ausgewählt werden, wenn der Rechnungsempfänger eingetragen wurde.";
	}
	return "";
}

type GetRevertBlockedReasonParams = {
	revertAction: InvoiceAction;
	revertBeforeSentAction: InvoiceAction;
	targetStatusId: InvoiceStatusId;
};

// The backend decides whether a revert is allowed. `revert` covers every target, `revertBeforeSent` covers
// a target before "Sent to client". Returns the backend reason of a refused revert, otherwise null.
export function getRevertBlockedReason({
	revertAction,
	revertBeforeSentAction,
	targetStatusId,
}: GetRevertBlockedReasonParams): string | null {
	if (!revertAction.allowed) return revertAction.reason ?? "";
	if (wipStatuses.includes(targetStatusId) && !revertBeforeSentAction.allowed) {
		return revertBeforeSentAction.reason ?? "";
	}
	return null;
}

type GetAdvanceBlockedReasonParams = {
	advanceAction: InvoiceAction;
	currentStatusOrder: number;
	targetStatusOrder: number;
};

// The backend decides whether an advance is allowed. Returns the backend reason of a refused advance for a
// target after the current status, otherwise null.
export function getAdvanceBlockedReason({
	advanceAction,
	currentStatusOrder,
	targetStatusOrder,
}: GetAdvanceBlockedReasonParams): string | null {
	if (targetStatusOrder <= currentStatusOrder || advanceAction.allowed) return null;
	return advanceAction.reason ?? "";
}
